import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Card, Typography, Spin, Divider, Row, Col, Button, Grid, Space, Tabs } from "antd";
import { selectMergedPortfolios, selectAllocationSummary } from "../../../store/slices/path.slice";
import StockChart from "../../../charts/StockChart";
import LogoCloud from "../../../components/Graphics/LogoCloud";
import Levels from "../../../components/Levels";
import NivoDonut from "../../../modules/Charts/Nivo/NivoDonut";
import CompanyBrand from "../../Company/CompanyBrand";
import { getAge } from "../../../utils/datetime";
import {selectPortfolioSimulations, selectLatestPortfolioSimulation} from "../../../store/slices/analytics.slice";
import usePathHelpers from "../usePathHelpers";
import { transformSimulationData } from "./pathSummaryTransforms";
import StatsTable from "./StatsTable";
import {money} from "../../../utils/strings";
import PopupEntry from "../../../components/Inputs/PopupEntry";
import {createNewPortfolio} from "../../../api/portfolioApi";
import {usePaths} from "../usePaths";
import DocumentHeader from "./DocumentHeader";
const { Text } = Typography;



const preparePortfolio = (portfolios, preferences, title)=>{
    const itemArray = [];
    portfolios.forEach((portfolio) => {
        console.log(portfolio);
        const {id, name, type, weight} = portfolio;
        itemArray.push({
            id, name, type, wgt: weight
        })
    });

    return {
        title,
        type: 'mm',
        itemArray,
        props: {
            preferences
        },
        category: 'custom'
    }

}


const PortfolioPathResults = () => {
  const { loading, error, data, responses } = useSelector(selectAllocationSummary);
  const dispatch = useDispatch();
  const portfolios = useSelector(selectMergedPortfolios);
  const {
    hasFetchedBlended,
    hasFetchedSimulations,
  } = usePaths(portfolios);
  const [title, setTitle] = useState('Custom Portfolio');
  const blendedSeries = useSelector(state => state.timeSeries.blendedSeries?.[state.timeSeries.blendedSeries.length - 1]);
  const {
        sortedPortfolios,
        aggregatedCategories,
        categoryColors,
        mergedTickerData,
        levelItems,
    } = usePathHelpers(portfolios);
  const age = responses?.info?.birthDate ? getAge(responses?.info?.birthYear) : null;
  const initialValue = responses?.info?.initialValue;
  const simulationData = useSelector(selectLatestPortfolioSimulation);

  console.log(blendedSeries);


  const handleFinish = ()=>{
      const preparedPortfolio = preparePortfolio(sortedPortfolios, responses, title);
      dispatch(createNewPortfolio(preparedPortfolio));
  }


  const pieData = (aggregatedCategories &&categoryColors) ? aggregatedCategories.map((category, index) => ({
      id: category.k,
      value: category.v,
      label: category.k,
      color: categoryColors[index]
  })).sort((a, b)=>b.value-a.value) : [];

  // Aggregate all portfolio summaries
    const summaries = sortedPortfolios.filter((portfolio) => portfolio.attr?.summary?.length>0).map((portfolio) => portfolio.attr.summary);
  if (loading) {
    return (
      <div className="w-full flex justify-center items-center p-8">
        <Spin size="large" tip="Loading portfolio allocations..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-6 bg-gradient-to-br from-red-50 to-red-100 rounded-xl">
        <Text className="text-red-600">Error loading portfolios: {error}</Text>
      </div>
    );
  }

  if (!portfolios.length) {
    return (
      <div className="w-full p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl">
        <Text className="text-gray-600">No portfolio allocations available</Text>
      </div>
    );
  }


  return (
      <div className="w-full">
          <div className={'max-w-3/4 w-3/4 pb-12 mb-12 mx-auto bg-white border-l-2 border-r-2 border-gray-100'}>
                      <DocumentHeader
         logo={true}                           // we want to show the logo
         name={responses?.info?.name}
         age={age}
         riskScore={responses?.info?.riskalyzeScore}
         title={title}
          onTitleChange={(text) => setTitle(text)}
          onFinish={handleFinish}
        />
              <Row align={'top'} gutter={64} className={'p-4'}>
                  <Col span={8}>
                      <div>
                          {summaries.map((summary, index) => (
                              <div className={'px-4 py-2'}>
                                  <div
                                      className="block text-sm mt-2 text-gray-800"
                                      style={{
                                          fontFamily: "Inter, sans-serif",
                                          display: '-webkit-box',
                                          WebkitLineClamp: '4',
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis'
                                      }}>
                                      {summary}
                                  </div>
                              </div>
                          ))}

                      </div>
                  </Col>
                  <Col span={16}>
                      <StatsTable data={blendedSeries?.summary}/>
                      <div className={'pt-6 mx-auto'}>
                          <LogoCloud srcs={mergedTickerData.filter(x => x?.logo?.length > 0).map(x => x.logo)} center={true}/>

                      </div>
                  </Col>
              </Row>
              <div className={'h-full mx-auto overflow-auto px-3 pb-12 '}>
                  <Tabs centered={true} defaultActiveKey={'1'}>
                      <Tabs.TabPane key={'1'} tab={'Illustrative Performance'}>
                          <div style={{width: '100%'}} className="py-3">
                              {Array.isArray(blendedSeries?.series) && blendedSeries?.series.length > 0 && (
                                      <StockChart height={400}
                                                  dataSeries={[{
                                                      plot: 0,
                                                      data: [{name: "Blended Series", data: blendedSeries?.series, stroke: "#1d4ed8"}
                                                      ],
                                                      xGrid: true,
                                                      yGrid: true,
                                                      secondYAxis: false,
                                                  },
                                                  ]}
                                                  showRangeSelector={false}
                                                  interactive={false}

                                      />
                              )}
                          </div>
                      </Tabs.TabPane>
                      <Tabs.TabPane key={'2'} tab={'Simulations'}>
                          {simulationData && simulationData.hasOwnProperty('series') && (
                              <div style={{width: '100%'}} className="py-3">
                                  <StockChart height={400}
                                              dataSeries={transformSimulationData(simulationData.series)}
                                              showRangeSelector={false}
                                              interactive={false}
                                    />
                              </div>
                          )}
                      </Tabs.TabPane>
                  </Tabs>
                  <Divider/>
                  <Tabs centered={true} defaultActiveKey={'1'}>
                      <Tabs.TabPane key={'1'} tab={'Assets List'}>
                          <Levels items={levelItems}
                                  title={`${responses?.info?.name + "'s" ?? 'Your'} Asset Allocation`}
                                  className={'hide-scrollbar'}
                                  showProgress={true}
                          />
                      </Tabs.TabPane>
                      {pieData.length > 0 && (
                          <Tabs.TabPane key={'2'} tab={'Pie Chart'}>
                              <div style={{height: '50vh', width: '100%'}} className={'mx-auto'}>
                                  <NivoDonut data={pieData}
                                             colors={categoryColors}
                                             centerText={'Categories'}
                                             showLegend={false}
                                             showLabels={true}
                                  />
                              </div>
                          </Tabs.TabPane>
                      )}
                  </Tabs>
                  <Divider/>
              </div>

          </div>
      </div>
  );
};

export default PortfolioPathResults;
